import { useCallback, useEffect, useRef, useState } from "react";
import logger from "../utils/logger";

// Gemini Live protocol constants (see audiobooks/voice_storybook.py on the backend).
const MIC_SAMPLE_RATE = 16000;
const PLAYBACK_SAMPLE_RATE = 24000;
const PING_INTERVAL_MS = 20000;
const RECONNECT_DELAY_MS = 2000;
const CREATING_PAGE_FLASH_MS = 1800;

export const VOICE_STATUS = {
  IDLE: "idle",
  CONNECTING: "connecting",
  LIVE: "live",
  PAUSED_UPGRADE: "paused_upgrade",
  COMPLETE: "complete",
  ERROR: "error",
  REPLACED: "replaced",
};

const encodePCM16 = (float32Array) => {
  const int16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const clamped = Math.max(-1, Math.min(1, float32Array[i]));
    int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return int16;
};

const downsampleBuffer = (buffer, inputSampleRate, outputSampleRate) => {
  if (outputSampleRate === inputSampleRate) return buffer;
  const ratio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < newLength) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
};

const base64ToInt16Array = (base64) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Int16Array(bytes.buffer);
};

const int16ArrayToFloat32 = (int16Array) => {
  const float32 = new Float32Array(int16Array.length);
  for (let i = 0; i < int16Array.length; i++) {
    const sample = int16Array[i];
    float32[i] = sample / (sample < 0 ? 0x8000 : 0x7fff);
  }
  return float32;
};

/** Diagnostics-only helpers — never touch playback / send paths. */
const voiceTimingLog = (event, extra = {}) => {
  logger.log("[VOICE TIMING]", event, {
    performanceNow: performance.now(),
    dateNow: Date.now(),
    ...extra,
  });
};

/**
 * Bridges the browser to the backend's Gemini Live voice-storybook WebSocket
 * (see POST /audiobooks/voice/ws-ticket + WS /audiobooks/voice/ws in
 * Pixley-Backend/audiobooks_new_router.py). The backend owns all Gemini
 * Live / session / star logic — this hook only streams mic audio, plays
 * back returned audio, and surfaces transcripts + lifecycle events.
 *
 * Connecting is NOT automatic — callers must invoke `start()` from a user
 * gesture (e.g. a "Start listening" tap) so browsers don't block mic/audio
 * playback under autoplay policies. This also doubles as the "resume"
 * affordance when reopening an in-progress AI Voice Storybook.
 */
export default function useVoiceStorybookSession({
  enabled,
  sessionId,
  apiClient,
  onPagePending,
  onPageCreated,
  onNeedsUpgrade,
  onUpgradeReady,
  onReady,
  onSessionComplete,
  onLimitUpgraded,
  onSessionReplaced,
  onError,
} = {}) {
  const [status, setStatus] = useState(VOICE_STATUS.IDLE);
  const [isMicOn, setIsMicOn] = useState(false);
  const [transcripts, setTranscripts] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  // Turn-taking hints for the compact mic control — "is Gemini's voice
  // currently playing back" and "did a page_created event just land".
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [isCreatingPage, setIsCreatingPage] = useState(false);

  const wsRef = useRef(null);
  const statusRef = useRef(VOICE_STATUS.IDLE);
  const micStreamRef = useRef(null);
  const micContextRef = useRef(null);
  const micSourceRef = useRef(null);
  const micProcessorRef = useRef(null);
  const playbackContextRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const activeSourcesCountRef = useRef(0);
  // Live AudioBufferSourceNodes, so a barge-in `interrupted` event can stop
  // queued (now stale) assistant audio immediately.
  const activeSourceNodesRef = useRef(new Set());
  const creatingPageTimeoutRef = useRef(null);
  // Set while a graceful teardown is letting the narrator's goodbye finish
  // playing before the playback context / WebSocket are closed.
  const drainCloseTimerRef = useRef(null);
  // Half-duplex mic gating so Gemini never hears its own voice played back
  // through the mic. Refs (not state) so onaudioprocess — a closure created
  // once in startMic() — always reads the live value, with no re-render lag.
  const isMicOnRef = useRef(false);
  const isAssistantSpeakingRef = useRef(false);
  const micSuppressedRef = useRef(false);
  const dropLoggedRef = useRef(false);
  // Latency diagnostics — refs only, never drive UI or playback.
  const timingRef = useRef({
    lastPcmSentPerf: null,
    lastPcmSentDate: null,
    wsAudioArrivedPerf: null,
    scheduledPerf: null,
    actuallyPlayingPerf: null,
    queueDelayMs: null,
    // True until the first audio chunk of an assistant turn is processed.
    awaitingFirstChunkOfTurn: true,
    // True until the first chunk of this turn actually starts playing.
    summaryPrintedForTurn: false,
  });
  const voiceStateRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const closingIntentionallyRef = useRef(false);
  const callbacksRef = useRef({});

  callbacksRef.current = {
    onPagePending,
    onPageCreated,
    onNeedsUpgrade,
    onUpgradeReady,
    onReady,
    onSessionComplete,
    onLimitUpgraded,
    onSessionReplaced,
    onError,
  };

  const setVoiceStatus = useCallback((next) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const logVoiceState = useCallback((nextState) => {
    if (voiceStateRef.current === nextState) return;
    voiceStateRef.current = nextState;
    const playbackCtx = playbackContextRef.current;
    voiceTimingLog(nextState, {
      audioContextCurrentTime: playbackCtx ? playbackCtx.currentTime : null,
      nextPlayTime: nextPlayTimeRef.current,
      wsReadyState: wsRef.current ? wsRef.current.readyState : null,
      activeSourcesCount: activeSourcesCountRef.current,
    });
  }, []);

  const resetTurnTiming = useCallback(() => {
    timingRef.current.awaitingFirstChunkOfTurn = true;
    timingRef.current.summaryPrintedForTurn = false;
    timingRef.current.wsAudioArrivedPerf = null;
    timingRef.current.scheduledPerf = null;
    timingRef.current.actuallyPlayingPerf = null;
    timingRef.current.queueDelayMs = null;
  }, []);

  const printLatencySummary = useCallback(() => {
    const t = timingRef.current;
    if (t.summaryPrintedForTurn) return;
    t.summaryPrintedForTurn = true;
    const fmt = (perf) => (perf == null ? "n/a" : `${perf.toFixed(1)} ms`);
    logger.log(
      [
        "============================",
        "VOICE LATENCY SUMMARY",
        `Last Mic PCM: ${fmt(t.lastPcmSentPerf)}`,
        `WS Audio Arrived: ${fmt(t.wsAudioArrivedPerf)}`,
        `Scheduled: ${fmt(t.scheduledPerf)}`,
        `Actually Playing: ${fmt(t.actuallyPlayingPerf)}`,
        `Queue Delay: ${t.queueDelayMs == null ? "n/a" : `${t.queueDelayMs.toFixed(1)} ms`}`,
        "============================",
      ].join("\n")
    );
  }, []);

  const stopMic = useCallback(() => {
    if (micProcessorRef.current) {
      micProcessorRef.current.disconnect();
      micProcessorRef.current.onaudioprocess = null;
      micProcessorRef.current = null;
    }
    if (micSourceRef.current) {
      micSourceRef.current.disconnect();
      micSourceRef.current = null;
    }
    if (micContextRef.current) {
      micContextRef.current.close().catch(() => {});
      micContextRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (isMicOnRef.current && !micSuppressedRef.current) {
      voiceTimingLog("last_pcm_sent", {
        timestamp: timingRef.current.lastPcmSentDate,
        lastPcmSentPerf: timingRef.current.lastPcmSentPerf,
      });
      logVoiceState("LISTENING_STOPPED");
    }
    isMicOnRef.current = false;
    setIsMicOn(false);
  }, [logVoiceState]);

  const startMic = useCallback(async () => {
    if (micStreamRef.current) return;
    try {
      // Explicit echoCancellation: with barge-in the mic stays open during
      // assistant playback, so the browser's AEC is what keeps Gemini from
      // hearing its own voice through the speakers.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      micStreamRef.current = stream;

      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextCtor();
      micContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      micSourceRef.current = source;

      // ScriptProcessorNode is deprecated but remains the most broadly
      // compatible way to get raw PCM frames without shipping a separate
      // AudioWorklet module file.
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      micProcessorRef.current = processor;

      // Barge-in: mic audio streams CONTINUOUSLY, including while the
      // assistant is speaking. Gemini Live runs server-side VAD and sends an
      // `interrupted` event when the child talks over the assistant (we flush
      // playback on it). Echo is handled by getUserMedia's echoCancellation —
      // the old half-duplex gate here made the app deaf during the assistant's
      // (long) turns, which read as "it isn't listening".
      processor.onaudioprocess = (event) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        const input = event.inputBuffer.getChannelData(0);
        const downsampled = downsampleBuffer(input, audioContext.sampleRate, MIC_SAMPLE_RATE);
        const pcm16 = encodePCM16(downsampled);
        wsRef.current.send(pcm16.buffer);
        // Track last outbound PCM for latency summary (no per-frame log spam).
        timingRef.current.lastPcmSentPerf = performance.now();
        timingRef.current.lastPcmSentDate = Date.now();
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
      // Inherit whatever the assistant is doing right now, in case the mic
      // is (re)started mid-playback (e.g. user un-mutes while Gemini is
      // still talking) — must not open the gate immediately in that case.
      micSuppressedRef.current = isAssistantSpeakingRef.current;
      dropLoggedRef.current = false;
      isMicOnRef.current = true;
      setIsMicOn(true);
      logger.log("[VOICE] Mic streaming started");
      voiceTimingLog("mic_started", {
        timestamp: Date.now(),
      });
      logVoiceState("MIC_STREAMING_STARTED");
      if (!micSuppressedRef.current) {
        logVoiceState("LISTENING_STARTED");
      }
    } catch (error) {
      logger.error("[VoiceStorybook] Microphone access failed:", error);
      setErrorMessage("Microphone access was denied. You can still type your answers below.");
    }
  }, [logVoiceState]);

  const toggleMic = useCallback(() => {
    if (isMicOn) stopMic();
    else startMic();
  }, [isMicOn, startMic, stopMic]);

  const playAudioChunk = useCallback((base64Data) => {
    try {
      if (!playbackContextRef.current) return;
      const audioContext = playbackContextRef.current;
      const t = timingRef.current;
      const isFirstChunkOfTurn = t.awaitingFirstChunkOfTurn;

      voiceTimingLog("audio_chunk_decode_start");

      const int16Array = base64ToInt16Array(base64Data);
      const float32Array = int16ArrayToFloat32(int16Array);

      const buffer = audioContext.createBuffer(1, float32Array.length, PLAYBACK_SAMPLE_RATE);
      buffer.copyToChannel(float32Array, 0);

      voiceTimingLog("audio_buffer_ready", {
        bufferDurationMs: buffer.duration * 1000,
        sampleCount: float32Array.length,
      });

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);

      activeSourcesCountRef.current += 1;
      activeSourceNodesRef.current.add(source);
      if (!isAssistantSpeakingRef.current) {
        isAssistantSpeakingRef.current = true;
        micSuppressedRef.current = true;
        dropLoggedRef.current = false;
        if (isMicOnRef.current) {
          logger.log("[VOICE] Mic streaming paused (assistant speaking)");
          // Emit the last PCM that made it out before the gate closed.
          voiceTimingLog("last_pcm_sent", {
            timestamp: timingRef.current.lastPcmSentDate,
            lastPcmSentPerf: timingRef.current.lastPcmSentPerf,
          });
          logVoiceState("LISTENING_STOPPED");
          logVoiceState("MIC_STREAMING_PAUSED");
        }
        logVoiceState("ASSISTANT_STARTED");
      }
      setIsAssistantSpeaking(true);

      source.onended = () => {
        voiceTimingLog("audio_finished", {
          activeSourcesRemaining: Math.max(0, activeSourcesCountRef.current - 1),
        });
        activeSourceNodesRef.current.delete(source);
        activeSourcesCountRef.current = Math.max(0, activeSourcesCountRef.current - 1);
        if (activeSourcesCountRef.current === 0) {
          isAssistantSpeakingRef.current = false;
          micSuppressedRef.current = false;
          setIsAssistantSpeaking(false);
          logVoiceState("ASSISTANT_FINISHED");
          if (isMicOnRef.current) {
            logger.log("[VOICE] Mic streaming resumed");
            logVoiceState("MIC_STREAMING_RESUMED");
            logVoiceState("LISTENING_STARTED");
          }
          resetTurnTiming();
        }
      };

      // Queue delay for the first chunk of a new assistant turn (before we bump nextPlayTime).
      const currentTime = audioContext.currentTime;
      const nextPlayTime = nextPlayTimeRef.current;
      if (isFirstChunkOfTurn) {
        t.awaitingFirstChunkOfTurn = false;
        const queueDelayMs = Math.max(0, (nextPlayTime - currentTime) * 1000);
        t.queueDelayMs = queueDelayMs;
        voiceTimingLog("queue_first_chunk", {
          audioContextCurrentTime: currentTime,
          nextPlayTime,
          queue_delay_ms: queueDelayMs,
        });
      }

      const startTime = Math.max(nextPlayTimeRef.current, audioContext.currentTime);
      if (isFirstChunkOfTurn) {
        t.scheduledPerf = performance.now();
      }
      voiceTimingLog("audio_source_scheduled", {
        startTime,
        audioContextCurrentTime: audioContext.currentTime,
        delayUntilStartMs: Math.max(0, (startTime - audioContext.currentTime) * 1000),
      });

      source.start(startTime);
      nextPlayTimeRef.current = startTime + buffer.duration;

      // AudioBufferSourceNode has no onstarted — schedule the closest equivalent.
      const delayUntilStartMs = Math.max(0, (startTime - audioContext.currentTime) * 1000);
      const startedForTurn = isFirstChunkOfTurn;
      window.setTimeout(() => {
        voiceTimingLog("audio_started_playing", {
          audioContextCurrentTime: playbackContextRef.current
            ? playbackContextRef.current.currentTime
            : null,
        });
        if (startedForTurn) {
          timingRef.current.actuallyPlayingPerf = performance.now();
          printLatencySummary();
        }
      }, delayUntilStartMs);
    } catch (error) {
      logger.error("[VoiceStorybook] Failed to play audio chunk:", error);
    }
  }, [logVoiceState, printLatencySummary, resetTurnTiming]);

  const sendText = useCallback((text) => {
    const trimmed = text?.trim();
    if (!trimmed || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "text", text: trimmed }));
    setTranscripts((prev) => [...prev, { role: "user", text: trimmed, id: `local-${Date.now()}-${prev.length}` }]);
  }, []);

  // Parent declined the continue purchase — best-effort wrap-up signal.
  // Backend also auto-finalizes on unexpected disconnect, so this is optional.
  const requestFinishStory = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "finish_story" }));
  }, []);

  // Tell the backend the new page is on-screen so Gemini can continue without
  // waiting for the 2.5s page_created fallback timeout.
  const notifyPageReady = useCallback((pageNumber) => {
    const n = Number(pageNumber);
    if (!Number.isFinite(n) || n <= 0) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "page_ready", page_number: n }));
  }, []);

  // Barge-in: the child spoke over the assistant. Gemini aborted its turn, so
  // everything still queued locally is stale — stop it and reopen the floor.
  const flushPlayback = useCallback(() => {
    for (const src of activeSourceNodesRef.current) {
      try {
        src.onended = null;
        src.stop();
      } catch {
        // already stopped
      }
    }
    activeSourceNodesRef.current.clear();
    activeSourcesCountRef.current = 0;
    nextPlayTimeRef.current = 0;
    isAssistantSpeakingRef.current = false;
    micSuppressedRef.current = false;
    setIsAssistantSpeaking(false);
    logVoiceState("ASSISTANT_INTERRUPTED");
    resetTurnTiming();
  }, [logVoiceState, resetTurnTiming]);

  // End button — clickable at any moment, including mid-narration. Stop any
  // playing/queued narration locally right away (the backend interrupts the
  // in-flight Gemini turn too), then the narrator says a warm goodbye and
  // finalizes the book; session_complete hands over to the finished-book UI.
  const requestEndStory = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "end_story" }));
    flushPlayback();
  }, [flushPlayback]);

  const handleServerMessage = useCallback((raw) => {
    let message;
    try {
      message = typeof raw === "string" ? JSON.parse(raw) : null;
    } catch {
      return;
    }
    if (!message || typeof message !== "object") return;

    if (message.type === "audio" && message.data) {
      const t = timingRef.current;
      if (t.awaitingFirstChunkOfTurn) {
        t.wsAudioArrivedPerf = performance.now();
      }
      voiceTimingLog("assistant_audio_received", {
        isFirstChunkOfTurn: t.awaitingFirstChunkOfTurn,
        dataLength: typeof message.data === "string" ? message.data.length : null,
      });
      playAudioChunk(message.data);
      return;
    }

    if (message.type === "transcript") {
      if ((message.role || "assistant") === "assistant") {
        voiceTimingLog("assistant_transcript_received", {
          textLength: (message.text || "").length,
        });
      }
      setTranscripts((prev) => [
        ...prev,
        { role: message.role || "assistant", text: message.text || "", id: `${message.role}-${Date.now()}-${prev.length}` },
      ]);
      return;
    }

    if (message.type === "pong") return;

    if (message.type === "error") {
      setErrorMessage(message.message || "Something went wrong with the live session.");
      setVoiceStatus(VOICE_STATUS.ERROR);
      callbacksRef.current.onError?.(message);
      return;
    }

    if (message.type === "event") {
      switch (message.event) {
        case "live_connected":
          setErrorMessage(null);
          setVoiceStatus(VOICE_STATUS.LIVE);
          break;
        case "interrupted":
          flushPlayback();
          break;
        case "page_pending":
          // Text is ready instantly — the UI flips to a text-only page while
          // the illustration + narration render in the background.
          setIsCreatingPage(true);
          if (creatingPageTimeoutRef.current) clearTimeout(creatingPageTimeoutRef.current);
          callbacksRef.current.onPagePending?.(message);
          break;
        case "page_created":
          if (creatingPageTimeoutRef.current) clearTimeout(creatingPageTimeoutRef.current);
          setIsCreatingPage(false);
          {
            const pageNumber =
              message?.page?.page_number ?? message?.page_number ?? null;
            // Wait for the UI to apply/switch the page (preload may delay),
            // then ACK so Gemini continues without the 2.5s timeout.
            Promise.resolve(callbacksRef.current.onPageCreated?.(message))
              .catch(() => {})
              .finally(() => {
                if (pageNumber != null) notifyPageReady(pageNumber);
              });
          }
          break;
        case "page_error":
          if (creatingPageTimeoutRef.current) clearTimeout(creatingPageTimeoutRef.current);
          setIsCreatingPage(false);
          callbacksRef.current.onError?.(message);
          break;
        case "needs_upgrade":
          // Soft signal only — Gemini may still be narrating page 8.
          // Do NOT open the upgrade popup or pause the UI yet.
          callbacksRef.current.onNeedsUpgrade?.(message);
          break;
        case "upgrade_ready":
          // Backend is ready for purchase after narration finished.
          setVoiceStatus(VOICE_STATUS.PAUSED_UPGRADE);
          callbacksRef.current.onUpgradeReady?.(message);
          break;
        case "ready":
          setVoiceStatus(VOICE_STATUS.COMPLETE);
          callbacksRef.current.onReady?.(message);
          break;
        case "session_complete":
          // Story is done (or was already finalized while we were away).
          // Do not reconnect — backend owns finalization.
          closingIntentionallyRef.current = true;
          setVoiceStatus(VOICE_STATUS.COMPLETE);
          callbacksRef.current.onSessionComplete?.(message);
          break;
        case "limit_upgraded":
          setVoiceStatus(VOICE_STATUS.LIVE);
          callbacksRef.current.onLimitUpgraded?.(message);
          break;
        case "session_replaced":
          closingIntentionallyRef.current = true;
          setVoiceStatus(VOICE_STATUS.REPLACED);
          setErrorMessage(message.message || "This story is being read in another tab.");
          callbacksRef.current.onSessionReplaced?.(message);
          break;
        default:
          break;
      }
    }
  }, [playAudioChunk, flushPlayback, setVoiceStatus, notifyPageReady]);

  // Full teardown. `drainPlayback: true` is the graceful variant for when the
  // story just completed: Gemini streams faster than real time, so several
  // seconds of the narrator's goodbye are usually still queued locally (and
  // `ready` can even race ahead of the last audio chunks on the WebSocket).
  // Mic and timers stop immediately, but the playback context and socket stay
  // alive until the goodbye finishes instead of cutting the narrator off.
  const disconnect = useCallback((options = {}) => {
    const drainPlayback = options.drainPlayback === true;
    closingIntentionallyRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (creatingPageTimeoutRef.current) {
      clearTimeout(creatingPageTimeoutRef.current);
      creatingPageTimeoutRef.current = null;
    }
    stopMic();
    dropLoggedRef.current = false;
    voiceStateRef.current = null;
    timingRef.current = {
      lastPcmSentPerf: null,
      lastPcmSentDate: null,
      wsAudioArrivedPerf: null,
      scheduledPerf: null,
      actuallyPlayingPerf: null,
      queueDelayMs: null,
      awaitingFirstChunkOfTurn: true,
      summaryPrintedForTurn: false,
    };
    setIsCreatingPage(false);

    const closeEverything = () => {
      if (drainCloseTimerRef.current) {
        clearInterval(drainCloseTimerRef.current);
        drainCloseTimerRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (playbackContextRef.current) {
        playbackContextRef.current.close().catch(() => {});
        playbackContextRef.current = null;
      }
      activeSourceNodesRef.current.clear();
      nextPlayTimeRef.current = 0;
      activeSourcesCountRef.current = 0;
      isAssistantSpeakingRef.current = false;
      micSuppressedRef.current = false;
      setIsAssistantSpeaking(false);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // ignore
        }
        wsRef.current = null;
      }
    };

    const wsStillOpen = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
    if (drainPlayback && playbackContextRef.current && (activeSourcesCountRef.current > 0 || wsStillOpen)) {
      // isAssistantSpeaking stays true while the goodbye drains, so the
      // celebration overlay keeps waiting; the last source's onended clears
      // it exactly when the narrator stops. Close once nothing is playing
      // AND the socket is gone (backend closes it after session_complete),
      // or after 5s of playback silence, or at a 20s hard deadline.
      if (drainCloseTimerRef.current) clearInterval(drainCloseTimerRef.current);
      let waitedMs = 0;
      let idleMs = 0;
      drainCloseTimerRef.current = setInterval(() => {
        waitedMs += 500;
        const playing = activeSourcesCountRef.current > 0;
        idleMs = playing ? 0 : idleMs + 500;
        const stillOpen = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
        if ((!playing && !stillOpen) || idleMs >= 5000 || waitedMs >= 20000) {
          closeEverything();
        }
      }, 500);
      return;
    }
    closeEverything();
  }, [stopMic]);

  const connectInternal = useCallback(async (currentSessionId) => {
    if (!apiClient || !currentSessionId) return;
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    closingIntentionallyRef.current = false;
    setErrorMessage(null);
    setVoiceStatus(VOICE_STATUS.CONNECTING);

    try {
      const ticketResponse = await apiClient.mintVoiceWsTicket(currentSessionId);
      const ticket = ticketResponse?.ticket;
      if (!ticket) {
        throw new Error(ticketResponse?.error || ticketResponse?.detail || "Could not start the live session.");
      }

      const wsBase = apiClient.getVoiceWsBaseUrl();
      const wsUrl = `${wsBase}/audiobooks/voice/ws?session_id=${encodeURIComponent(currentSessionId)}&ticket=${encodeURIComponent(ticket)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL_MS);
        startMic();
      };

      ws.onmessage = (event) => handleServerMessage(event.data);

      ws.onerror = (event) => {
        logger.error("[VoiceStorybook] WebSocket error:", event);
      };

      ws.onclose = () => {
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        stopMic();
        if (closingIntentionallyRef.current) return;
        // Already finalized (ready / session_complete) — do not resume chat.
        if (statusRef.current === VOICE_STATUS.COMPLETE) return;

        setVoiceStatus(VOICE_STATUS.ERROR);
        setErrorMessage("Live session disconnected. Reconnecting…");
        reconnectTimeoutRef.current = setTimeout(() => {
          connectInternal(currentSessionId);
        }, RECONNECT_DELAY_MS);
      };
    } catch (error) {
      logger.error("[VoiceStorybook] Failed to connect:", error);
      setVoiceStatus(VOICE_STATUS.ERROR);
      setErrorMessage(error?.message || "Could not connect to the live session.");
    }
  }, [apiClient, handleServerMessage, startMic, stopMic, setVoiceStatus]);

  const start = useCallback(() => {
    if (!sessionId) return;
    // A pending goodbye drain means the previous session is fully over —
    // reclaim the playback context for this session and drop the stale
    // audio / WebSocket before connecting.
    if (drainCloseTimerRef.current) {
      clearInterval(drainCloseTimerRef.current);
      drainCloseTimerRef.current = null;
      flushPlayback();
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // ignore
        }
        wsRef.current = null;
      }
    }
    // Create/resume the playback AudioContext synchronously inside this
    // user-gesture call so browsers don't block Gemini's voice audio.
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!playbackContextRef.current) {
      playbackContextRef.current = new AudioContextCtor();
      nextPlayTimeRef.current = 0;
    }
    if (playbackContextRef.current.state === "suspended") {
      playbackContextRef.current.resume().catch(() => {});
    }
    connectInternal(sessionId);
  }, [sessionId, connectInternal, flushPlayback]);

  const reconnect = useCallback(() => {
    disconnect();
    start();
  }, [disconnect, start]);

  // Reset to idle whenever the target session changes or the feature is
  // disabled (e.g. the story finished and the reader dropped voice mode).
  // Drain instead of hard-stopping: this flip fires the moment the backend
  // finalizes the book, which is typically while the narrator's goodbye is
  // still playing (or even still arriving) — never cut it off.
  useEffect(() => {
    if (!enabled) {
      disconnect({ drainPlayback: true });
      setVoiceStatus(VOICE_STATUS.IDLE);
      setTranscripts([]);
      setErrorMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionId]);

  // Best-effort "session ending" on tab close. Backend already auto-finalizes
  // on unexpected disconnect — this must not be required for correctness.
  useEffect(() => {
    if (!enabled) return undefined;
    const signalSessionEnding = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(JSON.stringify({ type: "end" }));
      } catch {
        // ignore — unload paths are best-effort only
      }
    };
    window.addEventListener("pagehide", signalSessionEnding);
    window.addEventListener("beforeunload", signalSessionEnding);
    return () => {
      window.removeEventListener("pagehide", signalSessionEnding);
      window.removeEventListener("beforeunload", signalSessionEnding);
    };
  }, [enabled]);

  useEffect(() => {
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    isMicOn,
    toggleMic,
    isAssistantSpeaking,
    isCreatingPage,
    transcripts,
    errorMessage,
    sendText,
    requestFinishStory,
    requestEndStory,
    notifyPageReady,
    start,
    reconnect,
    disconnect,
  };
}
